/**
 * Headless state machine for CRM inline editing.
 *
 * Shared by the table (editable-cell.tsx), overview rows, pipeline cards, and
 * the pipeline column rename. Owns all safety guards, accessibility state, and
 * commit semantics so those surfaces do not have to re-implement them.
 *
 * Escape semantics differ from the legacy editable-cell.tsx (which committed
 * on Escape): this hook ALWAYS discards on Escape. The second Escape after an
 * error-state Escape clears the error and discards.
 */

import type React from 'react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { isInsideFloatingOverlay } from '@/lib/base/overlays/floating-overlay';
import type { EditorKindDescriptor } from '@/lib/crm/inline-edit/editorKinds';
import { humanizeSaveError } from '@/lib/crm/utils/humanizeSaveError';
import { valuesEqual } from '@/lib/crm/utils/valuesEqual';
import { shouldIgnoreOutsideInteraction } from '@/utils/third-party-editor-ui';

import { isPopoverOpenWithin } from './isPopoverOpenWithin';

export type InlineEditStatus = 'idle' | 'editing' | 'saving' | 'error';

export interface UseInlineEditOptions {
  descriptor: EditorKindDescriptor;
  initialValue: unknown;
  onSave: (value: unknown) => Promise<void>;
  onClose?: () => void;
  containerRef: React.RefObject<Element | null>;
  triggerRef: React.RefObject<HTMLElement | null>;
  /**
   * An element that lives in the editor's own page, for the outside-click
   * test. Defaults to `containerRef`. A host that PORTALS its editor out of
   * its surface (the record row floating a rich-text field over the panel in
   * a CellFieldOverlay) must pass something that stays inside that surface —
   * its read cell, its trigger — or the surface's own overlay root (the
   * record slideout's dialog) no longer contains the editor, reads as a
   * floating layer above it, and every click elsewhere in the panel is
   * ignored instead of committing.
   */
  surfaceRef?: React.RefObject<Element | null>;
}

export interface UseInlineEditResult {
  isEditing: boolean;
  status: InlineEditStatus;
  draft: unknown;
  setDraft: (value: unknown) => void;
  errorMessage: string | null;
  announcement: string;
  startEdit: () => void;
  cancelEdit: () => void;
  commit: () => Promise<boolean>;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  handleBlur: (e: React.FocusEvent) => void;
  handlePointerDownOutside: (e: PointerEvent) => void;
}

export function useInlineEdit({
  descriptor,
  initialValue,
  onSave,
  onClose,
  containerRef,
  triggerRef,
  surfaceRef,
}: UseInlineEditOptions): UseInlineEditResult {
  const [status, setStatus] = useState<InlineEditStatus>('idle');
  const [draft, setDraftState] = useState<unknown>(initialValue);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const isEditing = status !== 'idle';

  // Re-entrancy guard: prevents blur + Enter from double-saving.
  const isSavingRef = useRef(false);
  // Suppresses blur-save after Escape cancel (blur fires when focus moves away).
  const isCancelledRef = useRef(false);
  // RAF handle for the deferred blur commit — cancelled on unmount to prevent
  // spurious saves when a virtualized row scrolls out of view.
  const blurRafRef = useRef<number | null>(null);
  // Set by commit/cancel; consumed by the idle-transition effect below.
  const pendingRefocusRef = useRef(false);
  // Parity toggle so an identical consecutive save still re-fires the aria-live
  // region. Setting the same string ('Saved') twice is a React no-op, so the
  // DOM text node never changes and the second save is never announced. A
  // parity-toggled trailing zero-width space (never spoken) changes the text
  // node without changing what a screen reader reads aloud. Starts at 0 so the
  // first announcement is exactly 'Saved'.
  const announceNonceRef = useRef(0);

  // Focus restore runs as a layout effect on the idle transition, not a
  // zero-delay timer. A surface whose trigger is a callback ref on its READ
  // mode element (InlineEditableFieldRow) only re-attaches that ref when
  // React re-renders, and a commit started outside a React event — the blur
  // RAF, the outside-pointerdown listener, a popover-close observer — renders
  // on a later task than setTimeout(0): the timer found a null ref and focus
  // fell to <body>. The effect runs once the read-mode element is mounted and
  // its ref attached. Layout (not passive) so it also lands before a closing
  // react-aria popover's own rAF focus restore, which only fires when focus
  // is still on <body>.
  useLayoutEffect(() => {
    if (status !== 'idle' || !pendingRefocusRef.current) {
      return;
    }
    pendingRefocusRef.current = false;
    triggerRef.current?.focus({ preventScroll: true });
  }, [status, triggerRef]);

  // Latest-value refs for deferred commit paths (blur RAF, document listener)
  // so they always save the CURRENT draft without stale closures.
  const draftRef = useRef<unknown>(initialValue);
  const initialValueRef = useRef<unknown>(initialValue);
  const statusRef = useRef<InlineEditStatus>('idle');
  const onSaveRef = useRef(onSave);
  const onCloseRef = useRef(onClose);

  // Sync all mutable values to refs every render.
  useEffect(() => {
    draftRef.current = draft;
  });
  useEffect(() => {
    initialValueRef.current = initialValue;
  });
  useEffect(() => {
    statusRef.current = status;
  });
  // Freeze onSave for the duration of an edit: the closure the caller passes
  // in (e.g. a decomposed association field's saver) captures
  // knownSubsetTargetIds/renderedIds as of render time, which must match what
  // the user was shown when startEdit() ran — not whatever is true when the
  // commit actually fires. Re-syncing unconditionally let a scoped save pick
  // up ids another user/automation added to the same subset mid-edit and
  // report them as removed. Only resync once idle, same gate as the
  // stale-draft resync below.
  useEffect(() => {
    if (!isEditing) {
      onSaveRef.current = onSave;
    }
  });
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  // Stale-draft resync: when initialValue changes while idle (e.g. a WebSocket
  // update from another user), keep the draft current so the next edit starts
  // from the latest value and doesn't silently overwrite the remote change.
  useEffect(() => {
    if (!isEditing) {
      setDraftState(initialValue);
      setErrorMessage(null);
    }
  }, [isEditing, initialValue]);

  // Core commit: saves valueToSave (draft when no override supplied), returns
  // true on success, false on save failure. Stable via commitRef below.
  const commitImpl = useCallback(
    async (override?: { value: unknown }): Promise<boolean> => {
      if (isSavingRef.current) {
        return true;
      }

      const valueToSave =
        override !== undefined ? override.value : draftRef.current;

      // Committing an unchanged draft is a no-op: close without calling onSave
      // (submitting it would be rejected by event validation).
      if (valuesEqual(valueToSave, initialValueRef.current)) {
        isSavingRef.current = true;
        setErrorMessage(null);
        setStatus('idle');
        onCloseRef.current?.();
        pendingRefocusRef.current = true;
        return true;
      }

      isSavingRef.current = true;
      setStatus('saving');

      try {
        await onSaveRef.current(valueToSave);
        // Toggle a trailing zero-width space on every other save so the
        // aria-live region re-announces an identical consecutive 'Saved'.
        const savedSuffix = String.fromCharCode(0x200b).repeat(
          announceNonceRef.current % 2
        );
        announceNonceRef.current += 1;
        setAnnouncement(`Saved${savedSuffix}`);
        setErrorMessage(null);
        setStatus('idle');
        onCloseRef.current?.();
        pendingRefocusRef.current = true;
        return true;
      } catch (err) {
        const msg =
          err instanceof Error
            ? humanizeSaveError(err.message)
            : 'Could not save. Try again.';
        setErrorMessage(msg);
        setStatus('error');
        isSavingRef.current = false; // Reset so retry is possible
        return false;
      }
    },
    []
  );

  // Latest-ref for commitImpl so deferred paths (blur RAF, pointer-down
  // listener) always call the freshest version without capturing it in closures.
  const commitRef = useRef(commitImpl);
  useEffect(() => {
    commitRef.current = commitImpl;
  });

  const startEdit = useCallback(() => {
    isCancelledRef.current = false;
    isSavingRef.current = false; // Reset so a fresh edit can save
    setDraftState(initialValueRef.current);
    setErrorMessage(null);
    setStatus('editing');
  }, []);

  const cancelEdit = useCallback(() => {
    isCancelledRef.current = true;
    if (blurRafRef.current !== null) {
      cancelAnimationFrame(blurRafRef.current);
      blurRafRef.current = null;
    }
    setDraftState(initialValueRef.current);
    setErrorMessage(null);
    setStatus('idle');
    onCloseRef.current?.();
    pendingRefocusRef.current = true;
  }, []);

  // Exposed commit (no override) for callers to chain navigation on.
  const commit = useCallback((): Promise<boolean> => commitRef.current(), []);

  const setDraft = useCallback(
    (value: unknown) => {
      setDraftState(value);
      draftRef.current = value; // Update immediately for pick-to-commit path
      if (descriptor.commitTrigger === 'pick') {
        void commitRef.current({ value });
      }
    },
    [descriptor.commitTrigger]
  );

  // Cancel any pending blur RAF on unmount to prevent spurious saves when the
  // row scrolls out of view in a virtualized list.
  useEffect(() => {
    return () => {
      if (blurRafRef.current !== null) {
        cancelAnimationFrame(blurRafRef.current);
      }
    };
  }, []);

  // Restore focus to the trigger on unmount if we were still editing (e.g.
  // a virtualized row unmounting while the editor is open).
  useEffect(() => {
    const trigger = triggerRef;
    return () => {
      if (statusRef.current !== 'idle') {
        trigger.current?.focus();
      }
    };
  }, [triggerRef]);

  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      // Focus moving within the container — not a real blur.
      if (containerRef.current?.contains(e.relatedTarget as Node)) {
        return;
      }
      // Already cancelled by Escape — suppress the save that blur would trigger.
      if (isCancelledRef.current) {
        isCancelledRef.current = false;
        return;
      }
      // Cancel any prior pending RAF (shouldn't stack, but be safe).
      if (blurRafRef.current !== null) {
        cancelAnimationFrame(blurRafRef.current);
      }
      // Defer commit one frame so portaled popovers (react-aria Select, date
      // picker) can complete their focus transfer. Without the deferral, blur
      // fires before the popover receives focus and we'd save prematurely.
      blurRafRef.current = requestAnimationFrame(() => {
        blurRafRef.current = null;
        // A keyboard handler already committed or cancelled — skip.
        if (isCancelledRef.current || isSavingRef.current) {
          return;
        }
        // Container gone (virtualized row scrolled away) — skip entirely.
        if (!containerRef.current) {
          return;
        }
        if (containerRef.current.contains(document.activeElement)) {
          return;
        }
        // Editor has an open popover — focus likely moved there, not outside.
        if (isPopoverOpenWithin(containerRef.current)) {
          return;
        }
        void commitRef.current();
      });
    },
    [containerRef]
  );

  const handlePointerDownOutside = useCallback(
    (e: PointerEvent) => {
      const target = e.target as Node;
      // Writing extension UI (Grammarly) or detached nodes must not close the editor.
      if (shouldIgnoreOutsideInteraction(target)) {
        return;
      }
      if (containerRef.current?.contains(target)) {
        return;
      }
      // The editor's picker popover is PORTALED, so its clicks are "outside"
      // containerRef. A click inside a floating overlay that does NOT contain
      // this editor (the picker's popover, a create-record modal opened from
      // it) is part of editing — never a commit. A click anywhere else commits
      // even while a picker is open: react-aria dismisses the picker on the
      // same interaction, and one outside click both closes the picker and
      // saves. (An overlay that DOES contain the editor — the record
      // slideout's own dialog — is just the page.) What counts as a floating
      // overlay is owned by lib/base/overlays/floating-overlay — never
      // re-derive it here from a role or a react-aria data attribute.
      // `surfaceRef` stands in for the editor when the editor itself is
      // portaled out of its page (see UseInlineEditOptions.surfaceRef).
      if (
        isInsideFloatingOverlay(target, {
          surface: surfaceRef?.current ?? containerRef.current,
        })
      ) {
        return;
      }
      void commitRef.current();
    },
    [containerRef, surfaceRef]
  );

  // Register the document pointer-down listener while editing. This catches
  // clicks that blur doesn't (e.g. multi-value card editors that remount their
  // input, dropping focus to body without a deliverable blur event).
  useEffect(() => {
    if (!isEditing) {
      return undefined;
    }
    document.addEventListener('pointerdown', handlePointerDownOutside, true);
    return () =>
      document.removeEventListener(
        'pointerdown',
        handlePointerDownOutside,
        true
      );
  }, [isEditing, handlePointerDownOutside]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        // Escape while saving — ignore (can't cancel an in-flight save).
        if (statusRef.current === 'saving') {
          return;
        }
        // First Escape after a failed save: clear the error, stay open so the
        // user can fix the value or press Escape again to discard.
        if (statusRef.current === 'error') {
          setStatus('editing');
          setErrorMessage(null);
          return;
        }
        // Always discard — Escape never commits.
        cancelEdit();
        return;
      }

      if (e.key === 'Enter') {
        // Editor owns Enter (e.g. textarea, multi-value list, association picker).
        if (descriptor.ownsEnterKey) {
          return;
        }
        // Open dropdown/popover needs Enter to confirm its selection.
        if (isPopoverOpenWithin(containerRef.current)) {
          return;
        }
        e.preventDefault();
        void commitRef.current();
      }
    },
    [descriptor.ownsEnterKey, containerRef, cancelEdit]
  );

  return useMemo(
    () => ({
      isEditing,
      status,
      draft,
      setDraft,
      errorMessage,
      announcement,
      startEdit,
      cancelEdit,
      commit,
      handleKeyDown,
      handleBlur,
      handlePointerDownOutside,
    }),
    [
      isEditing,
      status,
      draft,
      setDraft,
      errorMessage,
      announcement,
      startEdit,
      cancelEdit,
      commit,
      handleKeyDown,
      handleBlur,
      handlePointerDownOutside,
    ]
  );
}
